import { Grid2, Paper, Button, Typography } from '@mui/material';
import React, { useState } from 'react';
import MathGEMLogo from "../assets/MATHSGEM.png";
import SchoolIcon from '@mui/icons-material/School';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalculateIcon from '@mui/icons-material/Calculate';
import FunctionsIcon from '@mui/icons-material/Functions';
import SquareRootIcon from '@mui/icons-material/SquareFoot';
import PercentIcon from '@mui/icons-material/Percent';
import NumbersIcon from '@mui/icons-material/Numbers';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Box, styled } from '@mui/system';
import { useAuth } from "../AuthContext";
import { Link } from "react-router-dom";
import { motion } from 'framer-motion';
import Bubble1 from '../assets/Bubble1.png';
import Bubble2 from '../assets/Bubble2.png';
import Bubble3 from '../assets/Bubble3.png';
import StudentLearning from '../assets/StudentLearningImage.png';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';

const HeroSection = () => {
  const { user } = useAuth();
  const [hoveredBubble, setHoveredBubble] = useState(null);

  const handleScroll = () => {
    const nextSection = document.getElementById("learnmaths");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const AnimatedIcon = styled(SchoolIcon)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    transition: 'transform 0.3s ease-in-out',
    '.MuiButton-root:hover &': {
      animation: 'bounce 0.5s infinite',
    },
    '@keyframes bounce': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-5px)' },
    },
  }));

  const AnimatedArrowIcon = styled(ArrowDownwardIcon)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    transition: 'transform 0.3s ease-in-out',
    '.MuiButton-root:hover &': {
      animation: 'bounce 0.5s infinite',
    },
    '@keyframes bounce': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-5px)' },
    },
  }));

  const AnimatedDashIcon = styled(TableChartIcon)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    transition: 'transform 0.3s ease-in-out',
    '.MuiButton-root:hover &': {
      animation: 'bounce 0.5s infinite',
    },
    '@keyframes bounce': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-5px)' },
    },
  }));

  const StyledCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: 16,
    textAlign: 'center',
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    border: '1px solid rgba(173, 223, 173, 0.3)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 8px 24px rgba(76, 175, 80, 0.2)',
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },
  }));

  const logoVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        duration: 0.5
      }
    }
  };

  const bubble1Variants = {
    float: {
      y: [0, -20, 0],
      x: [0, 5, -5, 10],
      transition: {
        y: { duration: 20, repeat: Infinity, ease: "easeInOut" },
        x: { duration: 22, repeat: Infinity, ease: "easeInOut" },
      },
    },
  };

  const bubble2Variants = {
    float: {
      y: [0, -40, 0],
      x: [0, 20, -20, 0],
      rotate: [0, 5, -5, 20],
      transition: {
        y: { duration: 24, repeat: Infinity, ease: "easeInOut" },
        x: { duration: 25, repeat: Infinity, ease: "easeInOut" },
      },
    },
  };

  const bubble3Variants = {
    float: {
      y: [0, -15, -30, -15, 0],
      x: [0, 10, 0, -10, 20],
      transition: {
        y: { duration: 27, repeat: Infinity, ease: "easeInOut" },
        x: { duration: 27, repeat: Infinity, ease: "easeInOut" },
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    }),
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: 0.4,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3,
      },
    },
  };

  const mathMessages = {
    bubble1: "Did you know? The number π (pi) is infinite and never repeats!",
    bubble2: "Fun fact: Zero is neither positive nor negative.",
    bubble3: "Math tip: The square of any even number is always even!",
  };

  const mathIcons = [
    { Icon: CalculateIcon, top: '10%', left: '5%', size: 40 },
    { Icon: SchoolIcon, top: '20%', left: '85%', size: 50 },
    { Icon: SquareRootIcon, top: '30%', left: '15%', size: 45 },
    { Icon: PercentIcon, top: '40%', left: '75%', size: 35 },
    { Icon: NumbersIcon, top: '50%', left: '10%', size: 60 },
    { Icon: CalculateIcon, top: '60%', left: '90%', size: 30 },
    { Icon: SchoolIcon, top: '70%', left: '20%', size: 55 },
    { Icon: SquareRootIcon, top: '80%', left: '80%', size: 40 },
    { Icon: PercentIcon, top: '15%', left: '60%', size: 50 },
    { Icon: NumbersIcon, top: '25%', left: '30%', size: 45 },
    { Icon: CalculateIcon, top: '35%', left: '95%', size: 35 },
    { Icon: SchoolIcon, top: '45%', left: '25%', size: 60 },
  ];

  const iconFloatVariants = {
    float: {
      y: [0, -15, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
        delay: Math.random() * 2,
      },
    },
  };

  const features = [
    {
      title: 'Interactive Learning',
      description: 'Engaging video lessons and interactive quizzes for Primary and Secondary students to master Maths topics with confidence.',
      icon: <VideoLibraryIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
    },
    {
      title: 'Practice Worksheets',
      description: 'Downloadable worksheets for all Maths topics, perfect for Primary and Secondary students to practice and excel.',
      icon: <AssignmentIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
    },
    {
      title: 'Solutions',
      description: 'Downloadable solutions for all worksheets, perfect for Primary and Secondary students to practice and excel.',
      icon: <EmojiObjectsIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
    },
  ];

  return (
    <div style={{
      position: 'relative',
      top: -100,
      width: '100%',
      minHeight: '120vh', // Extended to accommodate both sections
      backgroundImage: 'linear-gradient(190deg, #27a770 0%, rgba(239, 239, 239, 0.7) 50%)',
      backgroundSize: 'cover',
      backgroundPosition: 'top',
      backgroundRepeat: 'no-repeat',
    }}>
      {mathIcons.map(({ Icon, top, left, size }, index) => (
        <motion.div
          key={index}
          variants={iconFloatVariants}
          animate="float"
          style={{
            position: 'absolute',
            top,
            left,
            zIndex: 1,
          }}
        >
          <Icon
            sx={{
              fontSize: size,
              color: 'rgba(255, 255, 255, 0.2)',
              pointerEvents: 'none',
            }}
          />
        </motion.div>
      ))}

      {/* Hero Section Content */}
      <Paper
        elevation={0}
        sx={{
          marginLeft: { md: 15, xs: 3 },
          padding: { md: 5, xs: 0 },
          width: { md: '80%', xs: '90%' },
          position: 'relative',
          background: 'transparent',
        }}
        style={{
          borderRadius: 20,
          marginTop: 80,
          zIndex: 2,
        }}
      >
        <br/>
        <br/>
        <br/>
        <Grid2 container spacing={2}>
          <Grid2
            sx={{ marginTop: 5, marginLeft: 5, color: 'black' }}
            size={{ xs: 12, md: 5 }}
          >
            <div>
              <Box
                elevation={0}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  textAlign: 'center',
                  marginLeft: -5,
                }}
              >
                <img
                  style={{
                    display: 'block',
                    height: 'auto',
                    margin: '0 auto',
                  }}
                  width={250}
                  src={MathGEMLogo}
                  alt="Math Gem Seychelles"
                />
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: 30,
                  display: { xs: 'none', md: 'block' },
                  color: 'black',
                  fontWeight: 700,
                }}
              >
                <b>Welcome to <br />MATHS GEM SEYCHELLES</b>
              </Typography>
            </div>

            <br />
            <br />
            <p style={{ fontSize: 22, color: 'black' }}>
              Maths Gem Seychelles – Polishing Minds, Making Students Shine!✨📚
            </p>
            <br />
            <br />
            <Grid2
              container
              size={12}
              display="flex"
              justifyContent="left"
              alignItems="left"
              gap={2}
            >
              {user ? (
                <Link to="/dashboard">
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={{
                      borderColor: 'black',
                      color: 'black',
                      '&:hover': {
                        borderColor: 'black',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    View Dashboard
                    <AnimatedDashIcon />
                  </Button>
                </Link>
              ) : (
                <Link to="/sign-up">
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={{
                      borderColor: 'black',
                      color: 'black',
                      '&:hover': {
                        borderColor: 'black',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    Join Now
                    <AnimatedIcon />
                  </Button>
                </Link>
              )}

              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderColor: 'black',
                  color: 'black',
                  '&:hover': {
                    borderColor: 'black',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                onClick={handleScroll}
              >
                See More
                <AnimatedArrowIcon />
              </Button>
            </Grid2>
          </Grid2>

          <Grid2
            size={{ xs: 12, md: 6 }}
            sx={{ display: { md: 'flex', xs: 'none' }, justifyContent: 'center' }}
          >
            <motion.img
              variants={logoVariants}
              initial="hidden"
              animate="visible"
              sx={{
                width: 250,
                maxWidth: '100%',
                height: 'auto',
                marginLeft: { xs: 0, md: 10 },
              }}
              width={400}
              height={400}
              src={MathGEMLogo}
              alt="Math Gem Seychelles"
            />
          </Grid2>
        </Grid2>
        <br />
      </Paper>

      {/* Features Section Content */}
      <Box
        sx={{
          padding: { xs: 3, md: 6 },
          position: 'relative',
          zIndex: 2,
          mt: 8, // Space between Hero and Features
        }}
      >
        

        <Grid2 id="learnmaths" container spacing={4} alignItems="center" justifyContent="center">
          {/* Cards Section */}
          <Grid2 size={{ xs: 12, md: 12 }}>
          <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            color: 'black',
            
            fontSize: { xs: 28, md: 36 },
          }}
        >
          Learn Maths with Maths Gem Seychelles
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            textAlign: 'center',
            color: 'black',
            mb: 6,
            maxWidth: 600,
            mx: 'auto',
            fontSize: { xs: 16, md: 18 },
            mt: 5,
            mb: 10,
          }}
        >
          Comprehensive resources for Primary and Secondary students, including video lessons, quizzes, and worksheets for all Maths topics.
        </Typography>
            <Grid2 container spacing={4} justifyContent="center">
              {features.map((feature, index) => (
                <Grid2 key={index} size={{ xs: 12, sm: 12, md: 4 }}>
                  <motion.div
                    custom={index}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    
                      <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: 'black', mb: 1 }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#424242', mb: 3, lineHeight: 1.6 }}
                      >
                        {feature.description}
                      </Typography>
                  </motion.div>
                </Grid2>
              ))}
            </Grid2>
          </Grid2>

          
        </Grid2>

        
      </Box>

      <br />
      <br />
    </div>
  );
};

export default HeroSection;